import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import useAuthStore from '../store/authStore';
import { MUNICIPIOS, AREAS } from '../utils/cidades';
import {
  X, Globe, Phone, Mail, MapPin, Briefcase,
  Building2, ExternalLink, ChevronRight, Loader2, RefreshCw
} from 'lucide-react';

const API      = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001';

// ── Logo clicável ─────────────────────────────────────────────
function LogoEmpresa({ src, nome, onClick }) {
  const [err, setErr] = useState(false);
  const base = 'w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 cursor-pointer transition-all hover:scale-105 hover:ring-2 hover:ring-acre-green hover:ring-offset-1 overflow-hidden';
  if (!src || err) return (
    <div className={`${base} bg-gradient-to-br from-acre-green to-acre-teal text-white`}
      onClick={onClick} title="Ver perfil da empresa">
      {nome?.charAt(0).toUpperCase() || '🏢'}
    </div>
  );
  return (
    <div className={`${base} bg-white border border-gray-100`}
      onClick={onClick} title="Ver perfil da empresa">
      <img src={`${API_BASE}${src}`} alt={nome}
        className="w-full h-full object-contain p-1"
        onError={() => setErr(true)}/>
    </div>
  );
}

// ── Tempo atualizado ──────────────────────────────────────────
function TempoAtualizado({ data }) {
  const [label, setLabel] = useState('');
  useEffect(() => {
    const calc = () => {
      if (!data) return;
      const diff = Math.floor((new Date() - new Date(data)) / 1000);
      if (diff < 10)    setLabel('agora mesmo');
      else if (diff < 60)   setLabel(`há ${diff}s`);
      else if (diff < 3600) setLabel(`há ${Math.floor(diff/60)}min`);
      else setLabel(new Date(data).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}));
    };
    calc();
    const t = setInterval(calc, 5000);
    return () => clearInterval(t);
  }, [data]);
  return (
    <span className="flex items-center gap-1.5 text-xs text-gray-400">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/>
      Atualizado {label}
    </span>
  );
}

// ── Modal perfil da empresa ───────────────────────────────────
function ModalPerfilEmpresa({ vaga, onFechar }) {
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vaga) return;
    const id = vaga.empresa_id;
    const url = id ? `${API}/empresas/publica/${id}` : null;
    if (url) {
      fetch(url)
        .then(r => r.json())
        .then(d => { setEmpresa(d); setLoading(false); })
        .catch(() => { setEmpresa(fallback(vaga)); setLoading(false); });
    } else {
      setEmpresa(fallback(vaga));
      setLoading(false);
    }
  }, [vaga]);

  function fallback(v) {
    return {
      nome_fantasia: v.nome_fantasia, razao_social: v.razao_social,
      logo_url: v.logo_url, setor: v.setor,
      cidade: v.cidade_empresa || v.cidade_vaga,
      descricao: v.desc_empresa, website: v.website,
    };
  }

  const nome = empresa?.nome_fantasia || empresa?.razao_social || '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onFechar()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header verde */}
        <div className="bg-gradient-to-r from-acre-dark to-[#1a3820] p-5 rounded-t-2xl relative">
          <button onClick={onFechar}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white/80 hover:text-white transition-colors">
            <X size={15}/>
          </button>

          {loading ? (
            <div className="flex items-center gap-3 pr-10">
              <div className="w-14 h-14 rounded-xl bg-white/10 animate-pulse flex-shrink-0"/>
              <div className="space-y-2 flex-1">
                <div className="h-4 w-40 bg-white/10 rounded animate-pulse"/>
                <div className="h-3 w-28 bg-white/10 rounded animate-pulse"/>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4 pr-10">
              <div className="w-14 h-14 rounded-xl bg-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center border border-white/20">
                {empresa?.logo_url
                  ? <img src={`${API_BASE}${empresa.logo_url}`} alt={nome} className="w-full h-full object-contain p-1"/>
                  : <Building2 size={24} className="text-white/50"/>}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-sora font-bold text-white text-lg leading-tight">{nome}</h2>
                {empresa?.razao_social && empresa.razao_social !== nome && (
                  <p className="text-white/50 text-xs mt-0.5 truncate">{empresa.razao_social}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {empresa?.setor && <span className="text-xs bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{empresa.setor}</span>}
                  {empresa?.cidade && <span className="text-xs flex items-center gap-1 text-white/60"><MapPin size={10}/>{empresa.cidade}, AC</span>}
                  {(empresa?.vagas_abertas || 0) > 0 && (
                    <span className="text-xs bg-acre-green/30 text-green-200 border border-acre-green/30 px-2 py-0.5 rounded-full font-semibold">
                      {empresa.vagas_abertas} vaga{empresa.vagas_abertas !== 1?'s':''} aberta{empresa.vagas_abertas!==1?'s':''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conteúdo */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={26} className="animate-spin text-acre-green"/></div>
        ) : (
          <div className="p-5 space-y-5">

            {/* Descrição */}
            {empresa?.descricao ? (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Sobre a empresa</p>
                <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-4">{empresa.descricao}</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <Building2 size={22} className="text-gray-200 mx-auto mb-2"/>
                <p className="text-sm text-gray-400">Descrição não disponível</p>
              </div>
            )}

            {/* Contato */}
            {(empresa?.website || empresa?.telefone_comercial || empresa?.email_comercial || empresa?.cidade) && (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Informações</p>
                <div className="space-y-2.5">
                  {empresa.cidade && (
                    <div className="flex items-center gap-2.5 text-sm text-gray-600">
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><MapPin size={13} className="text-gray-500"/></div>
                      <span>{empresa.cidade}, Acre — Brasil</span>
                    </div>
                  )}
                  {empresa.website && (
                    <a href={empresa.website.startsWith('http')?empresa.website:`https://${empresa.website}`}
                       target="_blank" rel="noreferrer"
                       className="flex items-center gap-2.5 text-sm text-acre-green hover:underline">
                      <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0"><Globe size={13} className="text-acre-green"/></div>
                      <span className="truncate">{empresa.website}</span><ExternalLink size={11} className="opacity-60 flex-shrink-0"/>
                    </a>
                  )}
                  {empresa.telefone_comercial && (
                    <div className="flex items-center gap-2.5 text-sm text-gray-600">
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0"><Phone size={13} className="text-gray-500"/></div>
                      <span>{empresa.telefone_comercial}</span>
                    </div>
                  )}
                  {empresa.email_comercial && (
                    <a href={`mailto:${empresa.email_comercial}`} className="flex items-center gap-2.5 text-sm text-blue-600 hover:underline">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><Mail size={13} className="text-blue-500"/></div>
                      <span>{empresa.email_comercial}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Outras vagas */}
            {empresa?.vagas_recentes?.filter(v => v.id !== vaga?.id).length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Outras vagas desta empresa</p>
                <div className="space-y-2">
                  {empresa.vagas_recentes.filter(v => v.id !== vaga?.id).slice(0,3).map((v,i) => (
                    <div key={v.id||i} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3 hover:border-acre-green/30 transition-colors">
                      <Briefcase size={14} className="text-acre-green flex-shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-acre-dark truncate">{v.titulo}</p>
                        <p className="text-xs text-gray-400">
                          {v.cidade_vaga} · {v.modalidade}{v.salario_min?` · R$ ${Number(v.salario_min).toLocaleString('pt-BR')}`:''}
                        </p>
                      </div>
                      <ChevronRight size={13} className="text-gray-300 flex-shrink-0"/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <Link to="/cadastro?tipo=candidato"
              className="btn-primary w-full justify-center py-3 flex items-center gap-2 text-sm">
              🚀 Cadastre-se e candidate-se gratuitamente
            </Link>
            <p className="text-center text-xs text-gray-400">Perfil criado em minutos com IA · 100% gratuito</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function VagasPublicas() {
  const { user } = useAuthStore();
  const [vagas,   setVagas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [ultima,  setUltima]  = useState(null);
  const [total,   setTotal]   = useState(0);
  const [filtros, setFiltros] = useState({ cidade:'', area:'', modalidade:'', busca:'' });
  const [vagaSel, setVagaSel] = useState(null);
  const pollingRef = useRef(null);

  const buscar = useCallback(async (silent=false) => {
    if (!silent) setLoading(true);
    const p = new URLSearchParams({ limit:20 });
    if (filtros.cidade)     p.set('cidade',    filtros.cidade);
    if (filtros.area)       p.set('area',       filtros.area);
    if (filtros.modalidade) p.set('modalidade', filtros.modalidade);
    try {
      const r = await fetch(`${API}/vagas?${p}`);
      const d = await r.json();
      let lista = d.vagas || [];
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        lista = lista.filter(v =>
          v.titulo?.toLowerCase().includes(b) ||
          v.nome_fantasia?.toLowerCase().includes(b) ||
          v.razao_social?.toLowerCase().includes(b)
        );
      }
      setVagas(lista);
      setTotal(d.total||0);
      setUltima(new Date());
    } catch {}
    if (!silent) setLoading(false);
  }, [filtros]);

  useEffect(() => {
    buscar();
    pollingRef.current = setInterval(() => buscar(true), 30000);
    return () => clearInterval(pollingRef.current);
  }, [buscar]);

  const setF = (k,v) => setFiltros(f => ({...f,[k]:v}));

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      {vagaSel && <ModalPerfilEmpresa vaga={vagaSel} onFechar={() => setVagaSel(null)}/>}

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="font-sora text-2xl font-bold text-acre-dark">Vagas em Aberto</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-400">{total} vagas</span>
              <TempoAtualizado data={ultima}/>
            </div>
          </div>
          <button onClick={() => buscar()}
            className="text-xs text-acre-green border border-acre-green px-4 py-2 rounded-xl hover:bg-acre-light transition-all flex items-center gap-1.5">
            <RefreshCw size={13}/> Atualizar
          </button>
        </div>

        {/* Filtros */}
        <div className="card p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input className="input" placeholder="🔍 Buscar vaga ou empresa..."
              value={filtros.busca} onChange={e => setF('busca',e.target.value)}/>
            <select className="input" value={filtros.cidade} onChange={e => setF('cidade',e.target.value)}>
              <option value="">📍 Todas cidades</option>
              {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className="input" value={filtros.area} onChange={e => setF('area',e.target.value)}>
              <option value="">🎯 Todas áreas</option>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="input" value={filtros.modalidade} onChange={e => setF('modalidade',e.target.value)}>
              <option value="">🏢 Modalidade</option>
              <option value="presencial">Presencial</option>
              <option value="remoto">Remoto</option>
              <option value="hibrido">Híbrido</option>
            </select>
          </div>
        </div>

        {/* Aviso: clique na logo */}
        <div className="flex items-center gap-2 bg-acre-light/60 border border-acre-green/20 rounded-xl px-4 py-2.5 mb-4">
          <Building2 size={14} className="text-acre-green flex-shrink-0"/>
          <p className="text-xs text-gray-600">
            <span className="font-semibold text-acre-green">Dica:</span> Clique na logo ou no nome da empresa para ver o perfil completo.
          </p>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <div className="w-10 h-10 border-4 border-acre-green border-t-transparent rounded-full animate-spin"/>
            <p className="text-gray-400 text-sm">Carregando vagas...</p>
          </div>
        ) : vagas.length === 0 ? (
          <div className="card p-10 text-center"><p className="text-5xl mb-3">🔍</p><p className="text-gray-500">Nenhuma vaga encontrada.</p></div>
        ) : (
          <div className="space-y-3">
            {vagas.map(v => {
              const nomeEmpresa = v.nome_fantasia || v.razao_social;
              return (
                <div key={v.id} className="card p-5 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">

                    {/* LOGO — clicável */}
                    <div className="relative group flex-shrink-0">
                      <LogoEmpresa src={v.logo_url} nome={nomeEmpresa} onClick={() => setVagaSel(v)}/>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2.5 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow">
                        Ver perfil
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-sora font-bold text-acre-dark text-base leading-tight">{v.titulo}</h3>
                          {/* Nome empresa clicável */}
                          <button onClick={() => setVagaSel(v)}
                            className="text-sm text-gray-500 mt-0.5 hover:text-acre-green transition-colors flex items-center gap-1 group/btn">
                            <span>{nomeEmpresa}</span>
                            <span className="text-gray-300 group-hover/btn:text-acre-green">· {v.cidade_vaga}, AC</span>
                            <Building2 size={11} className="opacity-0 group-hover/btn:opacity-50 transition-opacity flex-shrink-0"/>
                          </button>
                        </div>
                        <span className="badge-green text-xs flex-shrink-0">Aberta</span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {(v.areas_contratacao||[]).slice(0,3).map(a=><span key={a} className="badge-green text-xs">{a}</span>)}
                        {v.modalidade && <span className="badge-gray text-xs capitalize">{v.modalidade}</span>}
                        {v.regime_contratacao && <span className="badge-gray text-xs uppercase">{v.regime_contratacao}</span>}
                      </div>

                      {(v.salario_min||v.salario_max) && (
                        <p className="text-sm text-acre-green font-bold mt-2">
                          💰 {v.salario_min?`R$ ${Number(v.salario_min).toLocaleString('pt-BR')}`:''}{v.salario_min&&v.salario_max?' – ':''}{v.salario_max?`R$ ${Number(v.salario_max).toLocaleString('pt-BR')}`:''}
                        </p>
                      )}

                      {v.beneficios && <p className="text-xs text-gray-400 mt-1">🎁 {v.beneficios}</p>}

                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-gray-300">{new Date(v.created_at).toLocaleDateString('pt-BR')}</span>
                        {v.visualizacoes > 0 && <span className="text-xs text-gray-300">👁 {v.visualizacoes}</span>}
                        <button onClick={() => setVagaSel(v)}
                          className="ml-auto text-xs text-acre-green/70 hover:text-acre-green flex items-center gap-1 transition-colors">
                          <Building2 size={11}/> Ver empresa
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {!user && (
          <div className="mt-8 bg-acre-dark rounded-2xl p-6 text-white text-center">
            <p className="text-2xl mb-2">🎯</p>
            <h3 className="font-sora font-bold text-lg mb-2">Veja seu % de compatibilidade!</h3>
            <p className="text-white/70 text-sm mb-4">Cadastre-se grátis e a IA Mikhal descobre quais vagas combinam com você.</p>
            <Link to="/cadastro?tipo=candidato" className="btn-gold inline-block text-sm px-6 py-2.5">
              ✨ Criar conta grátis com IA
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
